import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { useBudgetActions } from '#budget';
import type { ApplyBudgetActionPayload } from '#budget';
import { useEnvelopeSheetValue } from '#components/budget/envelope/EnvelopeBudgetComponents';
import { guardarRecuento } from '#components/mobile/avisos/avisosPush';
import { rutaFicha } from '#components/mobile/budget/useFichaCategoria';
import { Fila, Tarjeta } from '#components/mobile/inicio/comunes';
import type { PropsWidget } from '#components/mobile/inicio/comunes';
import { estilos } from '#components/mobile/inicio/inicio.estilos';
import { filasPorHacer } from '#components/mobile/inicio/porHacer';
import { haceCuanto } from '#components/mobile/inicio/sinCuadrar';
import type { SinCuadrar } from '#components/mobile/inicio/sinCuadrar';
import { HojaCuadre } from '#components/mobile/inicio/widgets/HojaCuadre';
import { useCuadre } from '#components/mobile/inicio/widgets/useCuadre';
import { useCuentasSinCuadrar } from '#components/mobile/inicio/widgets/useCuentasSinCuadrar';
import { useIngresosSinCategoria } from '#components/mobile/inicio/widgets/useIngresosSinCategoria';
import { usePagosVencidos } from '#components/mobile/inicio/widgets/usePagosVencidos';
import { usePorRevisar } from '#components/mobile/revisar/usePorRevisar';
import { Barquito } from '#components/mobile/ui/Barquito';
import { Boton } from '#components/mobile/ui/Boton';
import { Icono } from '#components/mobile/ui/Icono';
import { IconoCaja } from '#components/mobile/ui/IconoCaja';
import { Pildora } from '#components/mobile/ui/Pildora';
import { color, espacio } from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useAccounts } from '#hooks/useAccounts';
import { useFormat } from '#hooks/useFormat';
import { useNavigate } from '#hooks/useNavigate';
import { useOverspentCategories } from '#hooks/useOverspentCategories';
import { pushModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';
import { envelopeBudget } from '#spreadsheet/bindings';
import { registrarUso } from '#uso';

/**
 * Un descuadre solo cuenta si dura: al guardar un movimiento las sumas de la
 * hoja no llegan todas a la vez y por un instante podrían no cuadrar.
 */
const ESPERA_DESCUADRE_MS = 2000;

function useDescuadreEstable(descuadre: number): number {
  const [estable, setEstable] = useState(0);
  useEffect(() => {
    if (descuadre === 0) {
      setEstable(0);
      return;
    }
    const id = setTimeout(() => setEstable(descuadre), ESPERA_DESCUADRE_MS);
    return () => clearTimeout(id);
  }, [descuadre]);
  return estable;
}

/**
 * «Por hacer»: movimientos nuevos, con o sin categoría (una sola fila:
 * Revisar → lista «Por revisar»), pagos fijos atrasados (Ya pagado; tocar la fila abre la ficha de la
 * categoría: son domiciliados y el cargo llega del banco), categorías en
 * rojo (Cubrir), ingresos del mes sin categoría (que aún no cuentan para
 * asignar; Categorizar), dinero listo para asignar (Asignar → «Asignar el mes») y
 * sobreasignado (Corregir); al final, las cuentas con más de un mes sin
 * cuadrar con el banco (Cuadrar; si son más de 3, una fila que lleva a
 * Cuentas). Si no hay nada, «Todo en orden»; mientras carga,
 * un esqueleto. La insignia cuenta filas, no categorías.
 *
 * Tamaños: compacto = solo píldoras con los botones; normal = filas;
 * grande = además los nombres de las categorías en rojo.
 */
export function PorHacer({ tamano, month }: PropsWidget) {
  const { t } = useTranslation();
  const format = useFormat();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const applyBudgetAction = useBudgetActions();
  const onBudgetAction = useCallback(
    async (month: string, type: string, args?: unknown) => {
      applyBudgetAction.mutate({
        month,
        type,
        args,
      } as ApplyBudgetActionPayload);
    },
    [applyBudgetAction],
  );

  const { categories: overspentCategories, totalAmount: totalOverspending } =
    useOverspentCategories({ month });

  const toBudget = useEnvelopeSheetValue(envelopeBudget.toBudget) ?? 0;

  // Hoja «Cubrir» de dos toques (la misma que el aviso del Plan); con una
  // sola en rojo abre ya en «¿De dónde sale?».
  const onCover = useCallback(() => {
    dispatch(
      pushModal({
        modal: {
          name: 'cubrir',
          options: {
            month,
            onBudgetAction,
            categoriaInicial:
              overspentCategories.length === 1
                ? overspentCategories[0].id
                : null,
          },
        },
      }),
    );
  }, [dispatch, month, onBudgetAction, overspentCategories]);

  const onAssign = useCallback(() => {
    void navigate(`/asignar?month=${month}`);
  }, [month, navigate]);

  // «Corregir» (sobreasignado): el resumen del mes de Actual trae la opción
  // de cubrir lo sobreasignado desde otra categoría.
  const onFixOverbudgeted = useCallback(() => {
    dispatch(
      pushModal({
        modal: {
          name: 'envelope-budget-summary',
          options: { month, onBudgetAction },
        },
      }),
    );
  }, [dispatch, month, onBudgetAction]);

  const numberOfOverspent = overspentCategories.length;
  const { pendientes, cargando: cargandoRevisar } = usePorRevisar();
  const nuevos = pendientes.length;
  const { data: cuentas = [], isLoading: cargandoCuentas } = useAccounts();
  // Sin cuentas no hay nada que cuadrar ni que revisar, pero tampoco está
  // «todo en orden»: falta lo primero (como la maqueta de la travesía al
  // saltarla, que no crea cuentas).
  const sinCuentas = !cargandoCuentas && !cuentas.some(c => !c.closed);
  const {
    vencidos,
    cambios,
    cargando: cargandoVencidos,
    marcarPagado,
    aceptarCambio,
    ignorarCambio,
  } = usePagosVencidos();
  const nombreCuenta = (id: string) =>
    cuentas.find(c => c.id === id)?.name ?? '';
  const { importe: ingresosSinCategoria } = useIngresosSinCategoria(month);

  const sinCuadrar = useCuentasSinCuadrar();

  // Cuadre del presupuesto: solo sale si no cuadra (lo sin categoría no es
  // un fallo). Al registro de uso, solo que ha pasado: sin importes.
  const cuadre = useCuadre(month);
  const descuadre = useDescuadreEstable(cuadre?.descuadre ?? 0);
  const hayDescuadre = descuadre !== 0;
  const [verCuadre, setVerCuadre] = useState(false);
  const avisado = useRef(false);
  useEffect(() => {
    if (descuadre !== 0 && !avisado.current) {
      avisado.current = true;
      registrarUso('cuadre_descuadre');
    }
  }, [descuadre]);

  const filas = filasPorHacer({
    nuevos,
    vencidos: vencidos.map(v => v.id),
    cambiosCuenta: cambios.map(c => c.id),
    enRojo: numberOfOverspent,
    ingresosSinCategoria,
    toBudget,
    sinCuadrar,
    descuadre,
  });
  const pendientesN = filas.length;
  const cargando = cargandoRevisar || cargandoVencidos;

  // Lo que dirá la notificación de «lo que quedó pendiente» (solo se guarda
  // en este móvil; el servidor nunca lo ve).
  const sinCategoria = pendientes.filter(
    m => !m.is_parent && !m.category && !m.transfer_id,
  ).length;
  const nombreEnRojo =
    overspentCategories.length === 1 ? overspentCategories[0].name : null;
  useEffect(() => {
    if (!cargandoRevisar) {
      guardarRecuento({
        sinCategoria,
        enRojo: numberOfOverspent,
        nombreEnRojo,
        porRevisar: nuevos,
      });
    }
  }, [cargandoRevisar, sinCategoria, numberOfOverspent, nombreEnRojo, nuevos]);
  const nothingToDo = pendientesN === 0 && !cargando;
  const esqueleto = pendientesN === 0 && cargando;
  const compacto = tamano === 'compacto';
  const caja = compacto ? 36 : 44;

  // «Cuadrar»: el mismo cuadre que el menú de la cuenta (saldo del banco y
  // luego la cuenta con su banner de cuadre).
  const onCuadrar = useCallback(
    (id: string) =>
      dispatch(
        pushModal({
          modal: {
            name: 'account-reconcile',
            options: {
              accountId: id,
              onReconcile: (cantidad: number) =>
                void navigate(`/accounts/${id}?reconcile=${cantidad}`),
            },
          },
        }),
      ),
    [dispatch, navigate],
  );

  const textoSinCuadrar = (c: SinCuadrar) => {
    const hace = haceCuanto(c.dias);
    switch (hace.unidad) {
      case 'semanas':
        return t('{{name}}: not reconciled for {{count}} weeks', {
          name: c.nombre,
          count: hace.n,
        });
      case 'anos':
        return t('{{name}}: not reconciled for over {{count}} years', {
          name: c.nombre,
          count: hace.n,
        });
      default:
        return t('{{name}}: not reconciled for {{count}} months', {
          name: c.nombre,
          count: hace.n,
        });
    }
  };
  // Las filas de cuadre van al final: separadas si hay algo antes.
  const primeraSinCuadrar = filas.findIndex(
    f => f.tipo === 'sin-cuadrar' || f.tipo === 'sin-cuadrar-grupo',
  );

  const titulo = (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      <Trans>To do</Trans>
      {pendientesN > 0 && (
        <Pildora
          estado="neutro"
          style={{ minHeight: 22, padding: '0 8px', fontSize: 12 }}
        >
          {pendientesN}
        </Pildora>
      )}
    </View>
  );

  const nombresEnRojo = overspentCategories.map(c => c.name).join(', ');

  return (
    <Tarjeta titulo={titulo} data-testid="inicio-por-hacer" relleno={0}>
      <View style={{ padding: '6px 14px' }}>
        {nothingToDo && sinCuentas && (
          <Fila data-testid="por-hacer-sin-cuentas">
            <IconoCaja icono="wallet" size={caja} />
            <View style={estilos.filaTexto}>
              <Text style={estilos.filaSub}>
                <Trans>There are no accounts yet.</Trans>
              </Text>
              <Button
                variant="bare"
                onPress={() => void navigate('/accounts')}
                style={{
                  alignSelf: 'flex-start',
                  minHeight: 44,
                  padding: 0,
                  gap: 6,
                  color: color.accent,
                  fontWeight: 800,
                }}
              >
                <Icono nombre="plus" size={18} />
                <Trans>Add account</Trans>
              </Button>
            </View>
          </Fila>
        )}

        {nothingToDo && !sinCuentas && (
          <Fila>
            <View style={{ color: color.ok, flexShrink: 0 }}>
              <Barquito width={compacto ? 44 : 64} />
            </View>
            <View style={estilos.filaTexto}>
              <Text style={{ ...estilos.filaTitulo, color: color.ok }}>
                <Trans>All in order</Trans>
              </Text>
              {!compacto && (
                <Text style={estilos.filaSub}>
                  <Trans>Nothing pending this month.</Trans>
                </Text>
              )}
            </View>
          </Fila>
        )}

        {descuadre !== 0 && (
          <Fila data-testid="por-hacer-descuadre">
            <IconoCaja icono="alert" tono="rojo" size={caja} />
            <View style={estilos.filaTexto}>
              <PrivacyFilter>
                <Text style={{ ...estilos.filaTitulo, color: color.bad }}>
                  {descuadre < 0
                    ? t("The budget doesn't add up: {{amount}} missing", {
                        amount: format(-descuadre, 'financial'),
                      })
                    : t("The budget doesn't add up: {{amount}} too much", {
                        amount: format(descuadre, 'financial'),
                      })}
                </Text>
              </PrivacyFilter>
              {!compacto && (
                <Text style={estilos.filaSub}>
                  <Trans>Accounts and budget don't match</Trans>
                </Text>
              )}
            </View>
            <Boton
              zona={52}
              style={estilos.botonFila}
              variante="primario"
              onPress={() => setVerCuadre(true)}
            >
              <Trans>See</Trans>
            </Boton>
          </Fila>
        )}
        {verCuadre && cuadre && (
          <HojaCuadre
            datos={cuadre.datos}
            resultado={cuadre}
            onCerrar={() => setVerCuadre(false)}
          />
        )}

        {nuevos > 0 && (
          <Fila separada={hayDescuadre} data-testid="por-hacer-revisar">
            <IconoCaja icono="bell" tono="acento" size={caja} />
            <View style={estilos.filaTexto}>
              <Text style={estilos.filaTitulo}>
                {t('{{count}} new transactions', { count: nuevos })}
              </Text>
              {!compacto && (
                <Text style={estilos.filaSub}>
                  <Trans>Check them and approve them</Trans>
                </Text>
              )}
            </View>
            <Boton
              zona={52}
              style={estilos.botonFila}
              variante="primario"
              onPress={() => void navigate('/revisar')}
            >
              <Trans>Review</Trans>
            </Boton>
          </Fila>
        )}

        {vencidos.map((v, i) => (
          <Fila
            key={v.id}
            separada={hayDescuadre || nuevos > 0 || i > 0}
            data-testid="por-hacer-vencido"
          >
            {/* Domiciliado: el cargo llega del banco; tocar abre la ficha. */}
            <Button
              variant="bare"
              data-testid="por-hacer-vencido-abrir"
              onPress={() => void navigate(rutaFicha(v.id, v.mes))}
              style={{
                flex: 1,
                minWidth: 0,
                minHeight: 44,
                justifyContent: 'flex-start',
                gap: espacio.icono,
                padding: 0,
                textAlign: 'left',
                backgroundColor: 'transparent',
              }}
            >
              <IconoCaja icono="cal" tono="rojo" size={caja} />
              <View style={estilos.filaTexto}>
                <Text style={estilos.filaTitulo}>
                  {t('Late payment: {{name}}', { name: v.nombre })}
                </Text>
                <PrivacyFilter>
                  <Text style={estilos.filaSub}>
                    {format(v.importe, 'financial')}
                  </Text>
                </PrivacyFilter>
              </View>
            </Button>
            <Boton
              zona={52}
              variante="fantasma"
              data-testid="por-hacer-ya-pagado"
              onPress={() => marcarPagado(v.id, v.mes)}
            >
              <Trans>Already paid</Trans>
            </Boton>
          </Fila>
        ))}

        {cambios.map((c, i) => (
          <Fila
            key={`cambio-${c.id}`}
            separada={
              hayDescuadre || nuevos > 0 || vencidos.length > 0 || i > 0
            }
            data-testid="por-hacer-cambio-cuenta"
          >
            <IconoCaja icono="cal" tono="aviso" size={caja} />
            <View style={estilos.filaTexto}>
              <Text style={estilos.filaTitulo}>
                {t(
                  '{{category}}: the last charge came from {{account}}. Charge it from there from now on?',
                  { category: c.nombre, account: nombreCuenta(c.cuentaId) },
                )}
              </Text>
            </View>
            <Boton
              zona={52}
              style={estilos.botonFila}
              variante="primario"
              data-testid="por-hacer-cambio-si"
              onPress={() => aceptarCambio(c)}
            >
              <Trans>Yes, change</Trans>
            </Boton>
            <Boton
              zona={52}
              variante="fantasma"
              data-testid="por-hacer-cambio-no"
              onPress={() => ignorarCambio(c)}
            >
              <Trans>No</Trans>
            </Boton>
          </Fila>
        ))}

        {numberOfOverspent > 0 && (
          <Fila
            separada={
              hayDescuadre ||
              nuevos > 0 ||
              vencidos.length > 0 ||
              cambios.length > 0
            }
          >
            <IconoCaja icono="alert" tono="rojo" size={caja} />
            <View style={estilos.filaTexto}>
              <Text style={estilos.filaTitulo}>
                {t('{{count}} category in the red', {
                  count: numberOfOverspent,
                })}
              </Text>
              <PrivacyFilter>
                <Text style={estilos.filaSub}>
                  {tamano === 'grande' && nombresEnRojo
                    ? `${nombresEnRojo} · ${format(totalOverspending, 'financial')}`
                    : format(totalOverspending, 'financial')}
                </Text>
              </PrivacyFilter>
            </View>
            <Boton
              zona={52}
              style={estilos.botonFila}
              variante="primario"
              onPress={onCover}
            >
              <Trans>Cover</Trans>
            </Boton>
          </Fila>
        )}

        {ingresosSinCategoria > 0 && (
          <Fila
            separada={
              numberOfOverspent > 0 ||
              hayDescuadre ||
              nuevos > 0 ||
              vencidos.length > 0 ||
              cambios.length > 0
            }
            data-testid="por-hacer-ingresos-sin-categoria"
          >
            <IconoCaja icono="coins" tono="acento" size={caja} />
            <View style={estilos.filaTexto}>
              <PrivacyFilter>
                <Text style={estilos.filaTitulo}>
                  {t('{{amount}} of uncategorized income', {
                    amount: format(ingresosSinCategoria, 'financial'),
                  })}
                </Text>
              </PrivacyFilter>
              {!compacto && (
                <Text style={estilos.filaSub}>
                  <Trans>It doesn't count towards assigning yet</Trans>
                </Text>
              )}
            </View>
            <Boton
              zona={52}
              style={estilos.botonFila}
              variante="primario"
              onPress={() => void navigate('/accounts/uncategorized')}
            >
              <Trans>Categorize</Trans>
            </Boton>
          </Fila>
        )}

        {toBudget > 0 && (
          <Fila
            separada={
              numberOfOverspent > 0 ||
              hayDescuadre ||
              nuevos > 0 ||
              vencidos.length > 0 ||
              cambios.length > 0 ||
              ingresosSinCategoria > 0
            }
          >
            <IconoCaja icono="coins" tono="ok" size={caja} />
            <View style={estilos.filaTexto}>
              <PrivacyFilter>
                <Text style={estilos.filaTitulo}>
                  {t('{{amount}} ready to assign', {
                    amount: format(toBudget, 'financial'),
                  })}
                </Text>
              </PrivacyFilter>
              {!compacto && (
                <Text style={estilos.filaSub}>
                  <Trans>Give it a job this month</Trans>
                </Text>
              )}
            </View>
            <Boton
              zona={52}
              style={estilos.botonFila}
              variante="primario"
              onPress={onAssign}
            >
              <Trans>Assign</Trans>
            </Boton>
          </Fila>
        )}

        {toBudget < 0 && (
          <Fila
            separada={
              numberOfOverspent > 0 ||
              hayDescuadre ||
              nuevos > 0 ||
              vencidos.length > 0 ||
              cambios.length > 0 ||
              ingresosSinCategoria > 0
            }
          >
            <IconoCaja icono="alert" tono="rojo" size={caja} />
            <View style={estilos.filaTexto}>
              <PrivacyFilter>
                <Text style={{ ...estilos.filaTitulo, color: color.bad }}>
                  {t('Overbudgeted by {{amount}}', {
                    amount: format(-toBudget, 'financial'),
                  })}
                </Text>
              </PrivacyFilter>
            </View>
            <Boton
              zona={52}
              style={estilos.botonFila}
              variante="primario"
              onPress={onFixOverbudgeted}
            >
              <Trans>Fix</Trans>
            </Boton>
          </Fila>
        )}

        {filas.map((f, i) =>
          f.tipo === 'sin-cuadrar' ? (
            <Fila
              key={f.cuenta.id}
              separada={i > 0}
              data-testid="por-hacer-sin-cuadrar"
            >
              <IconoCaja icono="check" tono="aviso" size={caja} />
              <View style={estilos.filaTexto}>
                <Text style={estilos.filaTitulo}>
                  {textoSinCuadrar(f.cuenta)}
                </Text>
                {!compacto && i === primeraSinCuadrar && (
                  <Text style={estilos.filaSub}>
                    <Trans>Check the balance against the bank</Trans>
                  </Text>
                )}
              </View>
              <Boton
                zona={52}
                style={estilos.botonFila}
                variante="primario"
                onPress={() => onCuadrar(f.cuenta.id)}
              >
                <Trans>Reconcile</Trans>
              </Boton>
            </Fila>
          ) : f.tipo === 'sin-cuadrar-grupo' ? (
            <Fila
              key="sin-cuadrar"
              separada={i > 0}
              data-testid="por-hacer-sin-cuadrar"
            >
              <IconoCaja icono="check" tono="aviso" size={caja} />
              <View style={estilos.filaTexto}>
                <Text style={estilos.filaTitulo}>
                  {t('{{count}} accounts not reconciled for over a month', {
                    count: f.n,
                  })}
                </Text>
                {!compacto && (
                  <Text style={estilos.filaSub}>
                    <Trans>Check their balance against the bank</Trans>
                  </Text>
                )}
              </View>
              <Boton
                zona={52}
                style={estilos.botonFila}
                variante="primario"
                onPress={() => void navigate('/accounts')}
              >
                <Trans>See accounts</Trans>
              </Boton>
            </Fila>
          ) : null,
        )}

        {esqueleto && (
          <Fila data-testid="por-hacer-esqueleto">
            <View
              style={{
                width: caja,
                height: caja,
                borderRadius: 12,
                backgroundColor: color.surface2,
                flexShrink: 0,
              }}
            />
            <View style={{ ...estilos.filaTexto, gap: 6 }}>
              <View
                style={{
                  height: 14,
                  width: '60%',
                  borderRadius: 7,
                  backgroundColor: color.surface2,
                }}
              />
              {!compacto && (
                <View
                  style={{
                    height: 12,
                    width: '40%',
                    borderRadius: 6,
                    backgroundColor: color.surface2,
                  }}
                />
              )}
            </View>
          </Fila>
        )}
      </View>
    </Tarjeta>
  );
}
